import { NextResponse } from "next/server"

import { createSupabaseAdminClient } from "@/lib/supabase/admin"
import { createSupabaseServerClient } from "@/lib/supabase/server"

type SubmissionPayload = {
  title: string
  description: string
  ingredients: string
  steps: string
  authorName: string
  authorEmail: string
  notes?: string
}

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export async function POST(request: Request) {
  try {
    const body = (await request.json()) as Partial<SubmissionPayload>

    const payload: SubmissionPayload = {
      title: body.title?.trim() ?? "",
      description: body.description?.trim() ?? "",
      ingredients: body.ingredients?.trim() ?? "",
      steps: body.steps?.trim() ?? "",
      authorName: body.authorName?.trim() ?? "",
      authorEmail: body.authorEmail?.trim() ?? "",
      notes: body.notes?.trim() ?? "",
    }

    const errors: Partial<Record<keyof SubmissionPayload, string>> = {}

    if (payload.title.length < 4) {
      errors.title = "Назва рецепта має містити щонайменше 4 символи."
    }

    if (payload.description.length < 20) {
      errors.description = "Опис має містити щонайменше 20 символів."
    }

    if (payload.ingredients.length < 10) {
      errors.ingredients = "Додайте список інгредієнтів."
    }

    if (payload.steps.length < 20) {
      errors.steps = "Опишіть кроки приготування детальніше."
    }

    if (payload.authorName.length < 2) {
      errors.authorName = "Вкажіть ім’я довжиною щонайменше 2 символи."
    }

    if (!isValidEmail(payload.authorEmail)) {
      errors.authorEmail = "Вкажіть коректну email-адресу."
    }

    if (Object.keys(errors).length > 0) {
      return NextResponse.json(
        { ok: false, message: "Помилка валідації.", errors },
        { status: 400 }
      )
    }

    const supabaseServer = await createSupabaseServerClient()
    const {
      data: { user },
    } = await supabaseServer.auth.getUser()

    const supabase = createSupabaseAdminClient()

    const { data, error } = await supabase
      .from("recipe_submissions")
      .insert({
        user_id: user?.id ?? null,
        title: payload.title,
        description: payload.description,
        ingredients: payload.ingredients,
        steps: payload.steps,
        author_name: payload.authorName,
        author_email: payload.authorEmail,
        notes: payload.notes || null,
        status: "pending",
      })
      .select(
        "id, user_id, title, description, ingredients, steps, author_name, author_email, notes, status, created_at"
      )
      .single()

    if (error) {
      return NextResponse.json(
        { ok: false, message: error.message },
        { status: 500 }
      )
    }

    return NextResponse.json({
      ok: true,
      message: "Рецепт збережено в Supabase і поставлено в pending.",
      data,
    })
  } catch {
    return NextResponse.json(
      { ok: false, message: "Не вдалося обробити submission." },
      { status: 500 }
    )
  }
}